import { decamelizeKeys } from 'humps'
import { map } from 'lodash'

import { currentOrderUuid } from '../utils/orderNumbers'

import BaseRemote from './baseRemote'

export const instance = new BaseRemote(process.env.REACT_APP_BASE_URL)

class StadiumRemote {
  static deviceRoute = (isKiosk) => (isKiosk ? '/kiosk/v3/orders' : '/pos/orders')

  static createPosOrder(params = {}, isKiosk, callback = (response) => {}) {
    const response = instance.post(`${StadiumRemote.deviceRoute(isKiosk)}`, params)
    callback(response)

    return response
  }

  static getOrderDeltas = (venueUuid, sinceTimestamp) => {
    let payload = decamelizeKeys({
      params: { venueUuid },
    })

    if (sinceTimestamp) {
      payload.params.since_timestamp = sinceTimestamp
    }

    return instance.get('/pos/data_deltas/order', payload)
  }

  static getOrders = ({ venueUuid, standMenuUuids, updatedSince }) => {
    // These have to be sent in params. The backing library to Axios will not allow a body.
    const payload = decamelizeKeys({
      params: { venueUuid, standMenuUuids, updatedSince },
    })
    return instance.get('/pos/orders', payload)
  }

  static getOrderDetails = (orderUuid) => instance.get(`/pos/orders/${orderUuid}`)

  static updatePosOrder(uuid, params = {}, isKiosk) {
    return instance.put(`${StadiumRemote.deviceRoute(isKiosk)}/${uuid}`, params)
  }

  static fetchMenus = ({ organizationName, standUuids, paymentType }) => {
    let translatedPaymentType = paymentType ? (paymentType === 'fcc' ? 'freedompay' : 'shift4') : null
    var payload = {
      params: { organizationName, venueUuid: process.env.REACT_APP_VENUE_UUID, paymentType: translatedPaymentType },
    }

    if (standUuids !== undefined && standUuids !== null) {
      payload = {
        params: { organizationName, uuids: standUuids, paymentType: translatedPaymentType },
      }
    }

    return instance.get('/pos/stands', payload)
  }

  static fetchPrinters = () => {
    var payload = {
      params: { organizationName: process.env.REACT_APP_ORG, venueUuid: process.env.REACT_APP_VENUE_UUID },
    }

    return instance.get('/pos/printers', payload)
  }

  static addLineItems = (orderUuid, orderMenuItems, isKiosk) => {
    const payload = { orderUuid, orderMenuItems }
    return instance.post(`${StadiumRemote.deviceRoute(isKiosk)}/${orderUuid}/line_items`, payload)
  }

  static closeTabOrder = (orderId, receiptSignature, tipAmountInCents, payments, isKiosk) => {
    const payload = {
      uuid: orderId,
      receiptSignature,
      tipAmountInCents,
      payments,
    }

    return instance.post(`${StadiumRemote.deviceRoute(isKiosk)}/${orderId}/close`, payload)
  }

  static refundOrderItems = (orderUuid, reason, lineItemUuids) => {
    const payload = {
      orderUuid,
      reason,
      userDetails: {},
      lineItemUuids,
    }

    return instance.post('/pos/refund', payload)
  }

  static voidItems = (orderUuid, reason, lineItemUuids) => {
    const payload = {
      data: {
        orderUuid,
        reason,
        userDetails: {},
        lineItemUuids,
      },
    }

    return instance.delete(`/pos/orders/${orderUuid}/line_items`, payload)
  }

  static getEmployeeRoles = () => {
    return instance.get('/operator/employee_roles')
  }

  static getAttendants = (organizationName) => {
    const payload = {
      params: { organizationName, venueUuid: process.env.REACT_APP_VENUE_UUID },
    }

    return instance.get('/operator/attendants', payload)
  }

  static getAttendantSettings = (pinNumber) => {
    const payload = {
      params: { pinNumber, venueUuid: process.env.REACT_APP_VENUE_UUID },
    }

    return instance.get('/api/v1/attendant_settings', payload)
  }

  static getPromotions = (venueUuid, standMenuUuids) => {
    const payload = {
      params: { venueUuid, standMenuUuids },
    }

    return instance.get('/pos/promotions', payload)
  }

  static getRemoteOrderTotal = (
    orderMenuItems,
    standMenuUuid,
    promotions,
    configOverride = {},
    isKiosk,
    affiliations = [],
    userUuid = undefined,
  ) => {
    let payload = {
      affiliations,
      userUuid,
      promotions: !promotions ? undefined : map(promotions, (promotion) => ({ uuid: promotion.uuid })),
      orderMenuItems,
      standMenuUuid,
      taxExempt: false, // TODO: This may need to be removed since changing this flag resets any promotions in Moneyball
    }

    payload.uuid = currentOrderUuid()

    return instance.post(`${StadiumRemote.deviceRoute(isKiosk)}/total`, payload, configOverride)
  }

  static getSummaryReport = (deviceId, employeeUuid) => {
    const payload = {
      params: { deviceId, employeeUuid },
    }

    return instance.get('/pos/order_reports', payload)
  }

  static sendEmailJobToStadium = ({ orderUuid, emails, paymentUuid }) => {
    const payload = decamelizeKeys({
      emails,
      paymentUuid,
    })
    return instance.post(`/pos/orders/${orderUuid}/emails/PosOrderReceiptEmailJob`, payload)
  }

  static getTicketBalance = (ticketId) => {
    return instance.get(`/pos/tickets/${ticketId}`)
  }

  static getQRBalance = (qrCode) => {
    return instance.get(`/pos/nonces/${qrCode}`)
  }

  static getGiftCardBalance = (giftCardId) => {
    return instance.get(`/pos/gift_cards/${giftCardId}`)
  }

  // config requests
  static getConfig = () => instance.get(`/api/v1/app_device_configurations`)

  static createConfig = (params = {}) => {
    const payload = decamelizeKeys({
      ...params,
    })

    return instance.post('/api/v1/app_device_configurations', payload)
  }

  static updateConfig = (params = {}) => {
    const payload = decamelizeKeys({
      ...params,
    })

    return instance.put(`/api/v1/app_device_configurations?uuid=${params.uuid}`, payload)
  }

  static getSections = (standUuid) => {
    // If the stand UUID is undefined, don't attempt to make an API call, which would result in an
    // error due to a string being passed as a parameter.
    if (!standUuid) {
      return new Promise((resolve, reject) => {
        resolve({ data: [] })
      })
    }

    return instance.get(`/pos/stands/${standUuid}/sections`)
  }

  static getRows = (sectionName) => {
    return instance.get(`/pos/sections/${sectionName}/rows?venue_uuid=${process.env.REACT_APP_VENUE_UUID}`)
  }

  static getSeats = (sectionName, row) => {
    return instance.get(`/pos/sections/${sectionName}/rows/${row}/seats?venue_uuid=${process.env.REACT_APP_VENUE_UUID}`)
  }
}

export default StadiumRemote
